import { defineComponent as _defineComponent } from 'vue'
import { ImportObjectInfo } from 'farmdok-rest-api';
import Vue, { computed, onBeforeMount, ref } from 'vue';

import i18n from '@/initI18n';

import useActivitiesImports from '@/activities/importActivities/composables/useActivitiesImport';
import { Api } from '@/plugins/farmdokRestApi';
import ErrorUserMessage from '@/shared/components/ErrorUserMessage.vue';
import FileUploadTile from '@/shared/components/FileUploadTile.vue';
import MaxWidthContainer from '@/shared/components/MaxWidthContainer.vue';
import ButtonLink from '@/shared/components/buttons/ButtonLink.vue';
import useBackendResponse from '@/shared/composables/useBackendResponse';
import { getRestResponseData } from '@/shared/modules/restApiHelpers';
import store from '@/store';


export default /*#__PURE__*/_defineComponent({
  __name: 'ImportLoadDataUpload',
  setup(__props) {

const $t = i18n.translate;
const { state, goNext } = useActivitiesImports();
const { getUserErrorMessageFromResponse, getUserErrorMessageFromPartialSuccess } = useBackendResponse();
const { activityImportApi } = Vue.prototype.$api as Api;

/* -- Reactive -- */
const uploadedFiles = ref<ImportObjectInfo[]>([]);
const successfulFiles = ref<File[]>([]);
const failedFiles = ref<File[]>([]);
const userErrorMessage = ref<string | string[]>('');

// utilities
const infoModalVisible = ref(false);

/* -- Computed -- */
const uploadedFilesHasEntries = computed<boolean>(() => !!Object.values(uploadedFiles.value).length);

onBeforeMount(async () => {
  await fetchUploadFilesStatus();
  updateImportNextEnabled();
});

/* -- Methods -- */
async function fetchUploadFilesStatus() {
  state.isFetching = true;
  const responseData = await getUploadFilesStatus();
  if (responseData.status !== 'success' && responseData.status !== 'partialSuccess') {
    userErrorMessage.value = getUserErrorMessageFromResponse(responseData);
  } else if (responseData.status === 'partialSuccess') {
    userErrorMessage.value = getUserErrorMessageFromPartialSuccess(responseData.errors);
  }
  uploadedFiles.value = responseData.data.objects ?? [];
  successfulFiles.value = uploadedFiles.value.map<File>((importFile) => ({ name: importFile.name! } as File));
  setSelectedFile();
  state.isFetching = false;
}

async function upload(files: File[]) {
  state.isFetching = true;
  userErrorMessage.value = '';
  const responseData = await postUpload(files);
  if (responseData.status !== 'success' && responseData.status !== 'partialSuccess') {
    userErrorMessage.value = getUserErrorMessageFromResponse(responseData);
  } else if (responseData.status === 'partialSuccess') {
    userErrorMessage.value = getUserErrorMessageFromPartialSuccess(responseData.errors);
  }
  uploadedFiles.value = responseData.data.objects;
  successfulFiles.value = uploadedFiles.value.map<File>((file) => ({ name: file.name } as File));
  setSelectedFile();
  updateImportNextEnabled();
  state.isFetching = false;
}

async function reset() {
  state.isFetching = true;
  userErrorMessage.value = '';
  const responseData = await postReset();
  if (responseData.status !== 'success') {
    userErrorMessage.value = getUserErrorMessageFromResponse(responseData);
  } else {
    uploadedFiles.value = [];
    successfulFiles.value = [];
    state.selectedUploadFile = '';
  }
  updateImportNextEnabled();
  state.isFetching = false;
}

// API actions
async function getUploadFilesStatus() {
  try {
    const { data } = await activityImportApi.activityImportStatus({ source: 'UserUpload' });
    return getRestResponseData(data);
  } catch (error) {
    return getRestResponseData(error);
  }
}

async function postUpload(files: File[]) {
  try {
    const { data } = await activityImportApi.activityImportUpload({ files });
    return getRestResponseData(data);
  } catch (error) {
    failedFiles.value.push(...files);
    return getRestResponseData(error);
  }
}

async function postReset() {
  try {
    const { data } = await activityImportApi.activityImportReset({
      activityImportResetRequest: { source: 'UserUpload' },
    });
    return getRestResponseData(data);
  } catch (error) {
    return getRestResponseData(error);
  }
}

// setters
function setSelectedFile() {
  if (!state.selectedUploadFile && uploadedFilesHasEntries.value) {
    [state.selectedUploadFile] = Object.keys(uploadedFiles.value);
  }
}

function setUploadFile(file: File) {
  const index = successfulFiles.value.indexOf(file);
  const uploadFile = uploadedFiles.value[index];
  state.selectedUploadFile = uploadFile.id;
  store.commit('activities/importActivities/updateActivityImportMapDataRequestParameters', {
    objectIds: uploadFile.id,
  });
}

function updateImportNextEnabled() {
  if (!state.selectedUploadFile) state.importNextEnabled = false;
  const selectedFile = uploadedFiles.value.find((file) => file.id === state.selectedUploadFile);
  state.importNextEnabled = !selectedFile?.errorUserMessages?.length && !selectedFile?.warningUserMessages?.length;
}

// conditions
function filenameContainsTaskdata(files: File[]): string {
  const wronglyNamedFiles = files.some((file) => !file.name.toLowerCase().includes('taskdata'));
  if (wronglyNamedFiles) {
    return (
      $t(
        'Einzelne, vollständige TASKDATA-Dateien müssen die Bezeichnung "TASKDATA" beinhalten. Zusammengehörige Dateien können nur gezipped hochgeladen werden.',
      ) ?? ''
    );
  }
  return '';
}

return { __sfc: true,$t, state, goNext, getUserErrorMessageFromResponse, getUserErrorMessageFromPartialSuccess, activityImportApi, uploadedFiles, successfulFiles, failedFiles, userErrorMessage, infoModalVisible, uploadedFilesHasEntries, fetchUploadFilesStatus, upload, reset, getUploadFilesStatus, postUpload, postReset, setSelectedFile, setUploadFile, updateImportNextEnabled, filenameContainsTaskdata, ErrorUserMessage, FileUploadTile, MaxWidthContainer, ButtonLink }
}

})